import type { FC, ReactElement } from "react"

import isNumber from "lodash-es/isNumber"
import map from "lodash-es/map"
import slice from "lodash-es/slice"
import { useCss } from "react-use"

import { buildCssClassName } from "@/3514/utils"

import { Avatar } from "./avatar.component"
import { avatarStyles as classes } from "./avatar.styles"
import { EAvatarFallbackVariant, EAvatarSize, type TAvatarListUser as TUser } from "./avatar.types"

type TProps = {
    users: TUser[]
    count?: number
    size?: EAvatarSize
    withRestCount?: boolean
    leftOffset?: number
    fallbackVariant?: EAvatarFallbackVariant
    extraPartyFallbackVariant?: EAvatarFallbackVariant
}

const AvatarList: FC<TProps> = ({
    users,
    count = 8,
    size = EAvatarSize._25,
    withRestCount = false,
    leftOffset = undefined,
    fallbackVariant = EAvatarFallbackVariant.BlueColored,
    extraPartyFallbackVariant = EAvatarFallbackVariant.BlueColored
}: TProps): ReactElement => {
    const leftMarginOffset: string = useCss({ marginLeft: `-${leftOffset ?? 0}px` })

    const avatarClasses: string = buildCssClassName(
        classes.list.item({ withLeftMarginOffset: isNumber(leftOffset) }),
        isNumber(leftOffset) && leftMarginOffset
    )

    return (
        <div className={classes.list.container()}>
            {map<TUser, ReactElement>(
                slice<TUser>(users, 0, count),
                (user: TUser): ReactElement => (
                    <Avatar
                        key={`c-b-a-${user.id}`}
                        isGuest={user.isGuest}
                        isExtraParty={user.isExtraParty}
                        className={avatarClasses}
                        alt={user.firstName}
                        fallbackVariant={user.isExtraParty ? extraPartyFallbackVariant : fallbackVariant}
                        size={size}
                        url={user.photo}
                        userData={{ firstName: user.firstName, lastName: user.lastName }}
                        isLettered
                    />
                )
            )}
            {withRestCount && users.length > count && (
                <Avatar
                    key="c-b-a-exceeder"
                    className={avatarClasses}
                    size={size}
                    userData={{ firstName: "+", lastName: `${users.length - count}` }}
                    isLettered
                    isRestCounter
                />
            )}
        </div>
    )
}

AvatarList.displayName = "AvatarList"

export { AvatarList }
